import { ulid } from '@w/utils'
import type { ReactElement } from 'react'
import { createPageCommand } from './page-commands'
import { cardVisibleTo, learnerCards } from '../db/question-card-triggers'
import { queryOptions } from '@w/react-query/db'
import { runtimeMonitor } from '@w/runtime'
import { pageRouteParams } from './page-route-params'
import { contentResources, spaceTreeOptions, usablePage } from '../db/spaces-queries'
import { isUsablePage } from '../db/page-eligibility'
import { isReplicationOffline } from '../db/workspace-replication-errors'
import { flashcards as flashcardsTable } from '../db/workspace-tables'
import { bindCreateRouteWithContext, redirect } from '@w/react-router'
import type { RootTel } from '@w/tel'
import { openPageReadOptions, pageViewOptions } from './page-view'
import { cardsListOptions, trainingFlashcardOptions, trainingPageOptions } from './workspace-queries'
import type {
  FlashcardsFeRouterCtx,
  InitFlag,
} from './flashcards-fe.router-ctx'
import { LoginPage } from '../pages/login'
import { TrashPage } from '../pages/trash'
import { SettingsPage } from '../pages/settings'
import { WorkspaceSettingsPage } from '../pages/workspace-settings'
import { PageLayout, PagesDirectory } from '../pages/page'
import { DatabasePage } from '../databases/database-page'
import { AuthenticatedLayout } from './authenticated-layout'
import { Outlet } from './flashcards-fe.router-bindings'
import { CardsPage } from '../cards/cards-page'
import {
  TrainingEmptyPage,
  TrainingEndPage,
  TrainingPage,
} from '../pages/training'
import { loadTranslations } from '../i18n'
import { _ensureGetUserInCtx, _handleAuthMagicLinkCallback } from '@w/auth-fe'
import { s, type Schema } from '@w/schema'
import { isCardDataError } from '../dead-letter-queue/item-readers'
import { blockId } from '@w/content-blocks/src/block'
import { type CardKind } from '../db/schema'
import { preparedMediaOptions } from '../media/prepared-media-options'
import { storedMediaPaths } from '../media/media-paths'
import { getTrainingExcerptBlocks, getTrainingPromptBlock } from '../training/training-scoring'
import { pageBlocks, projectedPageBlocks } from '../pages/page-blocks'
import { listAdoptedRuleMembers } from '../training/training-rule-members'
import { FirstDownloadPage } from '../shell/first-download-page'
import { NotFoundPage } from '../shell/not-found-page'

const { createRootRoute, createRoute, createRouter } =
  bindCreateRouteWithContext<FlashcardsFeRouterCtx>()
/** The last opened page belongs to one user: another user on the device never follows it. */
export const lastPageKey = (userId: string) => `flashcards:last-page:${userId}`

export const rootRoute = createRootRoute({
  notFoundComponent: NotFoundPage,
  beforeLoad: async ({ ctx }) => {
    if (ctx.writes.phase === 'open') await ctx.writes.flush()
    await runOnce(ctx, 'root', async () => {
      await loadTranslations()
    })
  },
})

export const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
  beforeLoad: ({ ctx, tel }) => runtimeMonitor.run({ name: 'Open authenticated page', kind: 'navigation' }, async task => {
    task.update({ status: 'waiting', waitingFor: 'Verify session' })
    await _ensureGetUserInCtx({ ctx, redirect, tel })
    task.update({ waitingFor: 'Open local workspace resources' })
    await ctx._ensureUserResources?.(tel)

    const user = ctx.getUser()
    if (ctx.spaces.snapshot(user.id).resources.length === 0) return
    await runOnce(ctx, `authenticated-layout:${user.id}`, async () => {
      task.update({ waitingFor: 'Read initial page' })
      // initFlags is per-session, so this block reruns on every app load;
      // without the existence check each reload minted a new Untitled page.
      const db = ctx.db(privateSpace(ctx, user.id).space.id)
      const existingPage = await db.query`
        SELECT ${db.pages.id} FROM ${db.pages}
        WHERE ${db.pages.deletedAt.isNull()} AND ${usablePage(db.pages)}
        LIMIT 1
      `.executeOne({ schema: s.object({ id: s.string() }), tel })
      if (!existingPage) await createEmptyPage(ctx)
    })
  }),
})

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  component: LoginPage,
})

export const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'callback',
  searchSchema: s.object({ token: s.string().optional() }),
  loader: ({ ctx, search }) =>
    _handleAuthMagicLinkCallback({
      ctx,
      redirect,
      token: search.token,
      successPath: '/',
    }),
})

export const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'settings',
  loader: async ({ ctx, tel }) => {
    const user = ctx.auth.getUserOrNull()
    if (!user) return { workspaceId: null }

    ctx.getUser = ctx.auth.getUser
    await ctx._ensureUserResources?.(tel)

    const tracked = readLastPage(ctx.localStorage, user.id)
    if (tracked) return { workspaceId: ctx.spaces.get(tracked.spaceId).space.workspaceId }

    const page = await findDefaultPage(ctx)
    return { workspaceId: page ? ctx.spaces.get(page.spaceId).space.workspaceId : user.id }
  },
  component: SettingsPage,
})

export const rootPageRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/',
  searchSchema: s.object({ preview: s.string().optional() }),
  loader: async ({ ctx, search }) => {
    if (ctx.spaces.snapshot(ctx.getUser().id).resources.length === 0) return

    const tracked = readLastPage(ctx.localStorage, ctx.getUser().id)

    const navigate = (data: { id: string; spaceId: string }) =>
      redirect({
        to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
        params: pageRouteParams(ctx, data.spaceId, data.id),
        search: {
          ...(search.preview ? { preview: search.preview } : {}),
          ...(typeof window !== 'undefined' && window.innerWidth < 768
            ? { nav: '1' }
            : {}),
        },
        replace: true,
      })

    if (tracked && await trackedPageOpens(ctx, tracked)) {
      throw navigate(tracked)
    }

    const defaultPage = await findDefaultPage(ctx)

    if (defaultPage) {
      writeLastPage(ctx.localStorage, ctx.getUser().id, defaultPage)
      throw navigate(defaultPage)
    }

    const newPage = await createEmptyPage(ctx)
    throw navigate(newPage)
  },
  component: FirstDownloadPage,
})

/** The last page still opens when the open page's own read returns it live and usable; the page route then reuses
 * that read instead of decoding the same row again (resolvePageSource kept its own reader and decoded it twice). */
async function trackedPageOpens(ctx: FlashcardsFeRouterCtx, tracked: { id: string; spaceId: string }) {
  const resource = contentResources(ctx.spaces.snapshot(tracked.spaceId)).find(value => value.space.id === tracked.spaceId)
  if (!resource) return false
  const [page] = await resource.queryClient.fetchQuery(openPageReadOptions(ctx, { spaceId: tracked.spaceId, pageId: tracked.id }))
  return page !== undefined && page.deletedAt === null && isUsablePage(page)
}

async function createEmptyPage(ctx: FlashcardsFeRouterCtx) {
  const user = ctx.getUser()
  const resource = privateSpace(ctx, user.id)
  const result = await createPageCommand(ctx, {
    workspaceId: resource.space.workspaceId, pageId: ulid(),
    title: ctx.testSupport?.defaultPage?.title ?? 'Untitled page',
    ...(ctx.testSupport?.defaultPage?.translatable ? { translatable: true } : {}),
    ...(ctx.testSupport?.defaultPage?.readonly ? { readonly: true } : {}),
    blocks: ctx.testSupport?.defaultPage?.blocks ?? [{ id: blockId(), type: 'p', children: [] }],
  })
  // Offline (and always for the anonymous principal) the new page stays local and syncs on the next round
  // (flashcards-local-first.plan.md unit 1, red (a)): the home page never waits for the network.
  await resource.replication.sync().catch((error: unknown) => { if (!isReplicationOffline(error)) throw error })
  const page = { id: result.pageId, workspaceId: result.workspaceId }
  await ctx.testSupport?.afterDefaultPageCreated?.(ctx, page)
  const pointer = { id: page.id, spaceId: page.workspaceId }
  writeLastPage(ctx.localStorage, user.id, pointer)
  return pointer
}

export const trashRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/trash',
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId) },
  component: TrashPage,
})

export const workspaceSettingsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/settings',
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId) },
  searchSchema: s.object({
    section: s.enum(['spaces', 'members', 'progress', 'invitations', 'sync-problems']).optional(),
    spaceId: s.string().optional(),
  }),
  component: WorkspaceSettingsPage,
})

export const pageLayoutRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/spaces/$spaceId/pages',
  component: PagesOutlet,
})

/** Keep the route tree independent of the bindings' inferred router type. */
function PagesOutlet(): ReactElement {
  return <Outlet />
}

export const pagesDirectoryRoute = createRoute({
  getParentRoute: () => pageLayoutRoute,
  id: 'pages-directory',
  path: '/',
  component: PagesDirectory,
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId) },
})

export const pageRoute = createRoute({
  getParentRoute: () => pageLayoutRoute,
  path: '$pageId',
  component: PageLayout,
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId, true) },
  // qid: when set, the page scrolls to the question/cloze block with this qid
  // (e.g. opened from the Cards table).
  searchSchema: s.object({
    qid: s.string().optional(),
    blockId: s.string().optional(),
    focusTitle: s.string().optional(),
    preview: s.string().optional(),
    q: s.string().optional(),
    nav: s.string().optional(),
  }),
  loader: ({ ctx, params, tel }) => {
    const resource = contentResources(ctx.spaces.snapshot(params.workspaceId)).find(value => value.space.id === params.spaceId)
    if (!resource) {
      // Inaccessible links render the unavailable view without exposing content.
      // Keep the directory subscriber mounted so invitation acceptance can
      // publish the new replica. Preserve the failed loader diagnostic while
      // allowing the unavailable view to keep observing directory updates.
      tel.currentSpan.end({ error: new Error('Page unavailable') })
      return null
    }
    return resource.queryClient.fetchQuery(pageViewOptions(ctx, params, resource), { tel }).then(view => {
      writeLastPage(ctx.localStorage, ctx.getUser().id, { spaceId: params.spaceId, pageId: params.pageId })
      return view
    })
  },
})

export const databaseRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/spaces/$spaceId/databases/$table',
  component: DatabasePage,
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId, true) },
})

export const cardsRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/cards',
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId) },
  // `view=activity` opens the caller's own activity; no parameter names another learner (progress is private).
  searchSchema: s.object({ view: s.enum(['activity']).optional() }),
  loader: async ({ ctx, params, tel }) => {
    const resource = workspaceResource(ctx, params.workspaceId)
    const { queryClient } = resource
    const flashcardsOptions = cardsListOptions(resource)
    const pagesQueries = contentResources(ctx.spaces.snapshot(params.workspaceId)).map(resource => ({ resource, options: spaceTreeOptions(resource) }))
    await Promise.all([
      queryClient.load(flashcardsOptions, { tel }),
      ...pagesQueries.map(value => value.resource.queryClient.load(value.options, { tel })),
    ])
    return { flashcardsOptions, queryClient, pagesQueries, progressWorkspaceId: params.workspaceId, workspaceId: params.workspaceId }
  },
  component: CardsPage,
})

export const trainingRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/training',
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId) },
  searchSchema: s.object({
    total: s.string().optional(),
    recovery: s.string().optional(),
  }),
  loader: async ({ ctx, params, search }) => {
    const flashcardsOptions = trainableFlashcardsOptions(ctx, params)
    const queryClient = workspaceResource(ctx, params.workspaceId).queryClient
    const queue = await queryClient.fetchQuery(flashcardsOptions)
    if (search.recovery !== '2' && queue.length > 0) {
      // Queue birth (QUEUE.md §4.2): the sitting IS the queue. Session counters
      // are queue state, so going back does not un-answer a card; per-card
      // presentation is entry state, so going back restores the card as it was.
      throw redirect({
        to: '/$workspaceId/training/$flashcardId',
        queue: queue.map((flashcard) => ({
          workspaceId: params.workspaceId,
          flashcardId: flashcard.qid,
          ...(search.recovery ? { search: { recovery: search.recovery } } : {}),
        })),
        queueState: {
          answered: 0,
          errors: 0,
          total: queue.length,
        },
        prefetch: 1,
        // one route, one deck per workspace (§6)
        queueId: search.recovery
          ? `${params.workspaceId}:recovery`
          : params.workspaceId,
        replace: true,
      })
    }
    return { flashcardsOptions, queryClient }
  },
  component: TrainingEmptyPage,
})

export const trainingFlashcardRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/training/$flashcardId',
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId) },
  // Per-card presentation is ENTRY state (§1): revealed/graded must rewind with
  // back. Required defaults, so a cold load on a card URL still renders.
  stateSchema: s.object({
    resolution: s.default(s.string(), 'unanswered'),
    mark: s.default(s.number(), -1),
    // Per-part results of a multi-part question, in document order.
    partMarks: s.array(s.number()).optional(),
  }),
  searchSchema: s.object({ recovery: s.string().optional() }),
  loader: async ({ ctx, params, search, tel }) => {
    const queryClient = workspaceResource(ctx, params.workspaceId).queryClient
    const flashcardsOptions = workspaceFlashcardsOptions(ctx, params)
    const flashcardOptions = trainingFlashcardOptions(ctx, params)
    // Loaded here, not suspended on in the component: the card query is not
    // warmed by the page route, and suspending would flash the app fallback on
    // every advance. Prefetch (§4.7) warms the next card the same way.
    const flashcards = await queryClient.fetchQuery(flashcardOptions)
    const flashcard = flashcards[0]
    if (!flashcard) {
      throw redirect({
        to: '/$workspaceId/training',
        params: { workspaceId: params.workspaceId },
        search: { recovery: search.recovery === '1' ? '2' : '1' },
        replace: true,
      })
    }
    const content = workspaceResource(ctx, params.workspaceId)
    const pagesOptions = trainingPageOptions(ctx, { workspaceId: content.space.id, pageId: flashcard.pageId })
    const pages = await content.queryClient.fetchQuery(pagesOptions)
    let cardPage = pages.find(
      (candidate) => candidate.id === flashcard.pageId,
    )
    if (
      cardPage?.content.kind === 'lexiconEntry' &&
      getTrainingPromptBlock(
        pageBlocks(cardPage.content),
        flashcard.qid,
      ) === null
    ) {
      // An adopted-entry edit can finish after this page query was cached. A
      // newly minted card proves the cached entry is stale when its qid is
      // absent, so reload that generated query key before rendering the card.
      content.queryClient.removeQueries({
        queryKey: pagesOptions.queryKey,
        exact: true,
      })
      const refreshed = await content.queryClient.load({ ...pagesOptions }, { tel })
      cardPage = refreshed.data.find(candidate => candidate.id === flashcard.pageId)
    }
    if (cardPage) {
      const progressDb = workspaceResource(ctx, params.workspaceId).db
      const blocks = await projectedPageBlocks(cardPage.content, {
        rep: async qid => (await progressDb.query`SELECT ${progressDb.flashcardEvents.id} FROM ${progressDb.flashcardEvents}
          WHERE ${progressDb.flashcardEvents.qid.eq(qid)} AND ${progressDb.flashcardEvents.userId.eq(ctx.getUser().id)}`.execute({ id: progressDb.flashcardEvents.id })).length,
        members: (language, inflectionClass, rep) => listAdoptedRuleMembers(content.db, {
          workspaceId: params.workspaceId, language, inflectionClass, rep,
        }),
      }, undefined, flashcard.qid)
      const paths = storedMediaPaths(getTrainingExcerptBlocks(blocks, flashcard.qid))
      if (paths.length > 0) await ctx.queryClient.load(preparedMediaOptions(ctx.bucket, paths))
    }
    return { flashcardsOptions, pagesOptions, flashcardOptions, queryClient, workspaceId: content.space.workspaceId, contentSpaceId: content.space.id, progressWorkspaceId: params.workspaceId, contentQueryClient: content.queryClient }
  },
  component: TrainingPage,
})

export const trainingEndRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/$workspaceId/training/end',
  beforeLoad: async ({ ctx, params }) => { await openRouteWorkspace(ctx, params.workspaceId) },
  searchSchema: s.object({
    errors: s.string().optional(),
    total: s.string().optional(),
  }),
  loader: ({ ctx, params }) => ({
    flashcardsOptions: workspaceFlashcardsOptions(ctx, params),
  }),
  component: TrainingEndPage,
})

export const routeTree = rootRoute.addChildren([
  loginRoute,
  callbackRoute,
  settingsRoute,
  authenticatedLayoutRoute.addChildren([
    rootPageRoute,
    workspaceSettingsRoute,
    trashRoute,
    pageLayoutRoute.addChildren([pagesDirectoryRoute, pageRoute]),
    databaseRoute,
    cardsRoute,
    trainingRoute,
    trainingEndRoute,
    trainingFlashcardRoute,
  ]),
])

export function createFlashcardsFeRouter(ctx: FlashcardsFeRouterCtx) {
  return createRouter({
    routeTree,
    ctx,
    tel: ctx.tel as RootTel,
  })
}

export type FlashcardsFeRouter = ReturnType<typeof createFlashcardsFeRouter>

export async function runOnce(
  ctx: Pick<FlashcardsFeRouterCtx, 'initFlags'>,
  flag: InitFlag,
  fn: () => Promise<void>,
) {
  if (ctx.initFlags.has(flag)) return

  await fn()
  ctx.initFlags.add(flag)
}

/**
 * The card the URL designates, loaded BY ID and unfiltered. The due-list query
 * drops a card the moment it is reviewed, so a sitting must not read its current
 * card out of that list — INV-1: the URL alone selects the card.
 */
export type WorkspaceFlashcardRow = {
  id: string
  pageId: string
  qid: string
  cardKind: CardKind | null
  fractionalIndex: string
  frozenAt: Date | null
  deletedAt: Date | null
  repeatAt: Date | 'now'
  groupKey: string
  groupRank: Date | null
}

function workspaceFlashcardRowSchema() {
  return s.object({
    id: flashcardsTable.qid.zodType,
    pageId: flashcardsTable.pageId.zodType,
    qid: flashcardsTable.qid.zodType,
    cardKind: flashcardsTable.cardKind.zodType,
    fractionalIndex: flashcardsTable.fractionalIndex.zodType,
    frozenAt: flashcardsTable.frozenAt.zodType,
    deletedAt: flashcardsTable.deletedAt.zodType,
    repeatAt: flashcardsTable.repeatAt.zodType,
    groupKey: s.string(),
    groupRank: flashcardsTable.repeatAt.zodType.nullable(),
  }) as Schema<WorkspaceFlashcardRow>
}

/**
 * Ordering + grouping per decisions 6/7 (flashcards-lexicon-forms.decisions.md):
 * groups are lexicon card kinds ('k'+cardKind) or note pages ('p'+pageId),
 * ranked by the group's most-overdue member (MIN, CASE-guarded so frozen/
 * deleted rows never pollute a group's rank), with decision 6's kind order
 * surviving as the tiebreaker between equally-late groups via groupKey.
 *
 * `now` is bound fresh inside queryFn on every execution — never captured at
 * options-construction time — so vi.setSystemTime-driven refetches see cards
 * becoming due. SQLite's unixepoch() is deliberately not used: it is
 * invisible to fake timers.
 */
export function workspaceFlashcardsOptions(
  ctx: FlashcardsFeRouterCtx,
  params: { workspaceId: string },
) {
  const db = workspaceResource(ctx, params.workspaceId).db
  const user = ctx.getUser()
  // Not executed (queryFn below is overridden) — only used to derive a stable
  // queryKey and table dependency for cache invalidation. Structurally mirrors
  // the real query (frozenAt/deletedAt/repeatAt + ORDER BY) without binding
  // `now`, which must be evaluated fresh inside queryFn on every execution.
  const options = queryOptions(db.query`
    SELECT ${db.flashcards.qid} FROM ${learnerCards(user.id)}
    WHERE ${cardVisibleTo(db.flashcards, user.id)}
      AND (
        ${db.flashcards.frozenAt.isNotNull()}
        OR ${db.flashcards.deletedAt.isNotNull()}
        OR ${db.flashcards.repeatAt.isNotNull()}
      )
    ORDER BY ${db.flashcards.pageId}, ${db.flashcards.fractionalIndex}, ${db.flashcards.qid}
  `, { id: db.flashcards.qid })
  return {
    ...options,
    queryFn: async (): Promise<WorkspaceFlashcardRow[]> => {
      const now = new Date(ctx.clock.now())
      const query = db.query`
        WITH base AS (
          SELECT
            ${db.flashcards.qid} AS id,
            ${db.flashcards.pageId} AS pageId,
            ${db.flashcards.qid} AS qid,
            ${db.flashcards.cardKind} AS cardKind,
            ${db.flashcards.fractionalIndex} AS fractionalIndex,
            ${db.flashcards.frozenAt} AS frozenAt,
            ${db.flashcards.deletedAt} AS deletedAt,
            ${db.flashcards.repeatAt} AS repeatAt,
            CASE
              WHEN ${db.flashcards.cardKind} IS NOT NULL
                THEN 'k' || ${db.flashcards.cardKind}
              ELSE 'p' || ${db.flashcards.pageId}
            END AS groupKey
          FROM ${learnerCards(user.id)}
          WHERE ${cardVisibleTo(db.flashcards, user.id)}
            AND ${db.flashcards.deletedAt.isNull()} AND ${db.flashcards.graduated.eq(false)}
            AND ${db.flashcards.frozenAt.isNull()}
            AND (
              ${db.flashcards.frozenAt.isNotNull()}
              OR ${db.flashcards.deletedAt.isNotNull()}
              OR ${db.flashcards.repeatAt.eq('now')} OR ${db.flashcards.repeatAt.lte(now)}
            )
        )
        SELECT
          id, pageId, qid, cardKind, fractionalIndex, frozenAt,
          deletedAt, repeatAt, groupKey,
          MIN(CASE WHEN repeatAt <= ${now} THEN repeatAt END)
            OVER (PARTITION BY groupKey) AS groupRank
        FROM base
        ORDER BY
          (groupRank IS NULL), groupRank, groupKey, pageId, fractionalIndex, id
      `
      const schema = workspaceFlashcardRowSchema()
      const rows = await query.execute(s.unknown())
      // An undecodable card leaves the queue (DROPMALFORMED); its card reader shows the placeholder.
      return rows.flatMap(row => {
        try { return [schema.parse(row)] }
        catch (error) {
          if (!isCardDataError(error)) throw error
          return []
        }
      })
    },
  }
}

export function trainableFlashcardsOptions(
  ctx: FlashcardsFeRouterCtx,
  params: { workspaceId: string },
) {
  const options = workspaceFlashcardsOptions(ctx, params)
  return {
    ...options,
    queryKey: [...options.queryKey, 'trainable'],
    queryFn: async () => {
      const queue = await options.queryFn()
      const readable = await Promise.all(queue.map(async candidate => {
        const db = workspaceResource(ctx, params.workspaceId).db
        const source = await db.query`SELECT ${db.pages.id} FROM ${db.pages} WHERE ${db.pages.id.eq(candidate.pageId)}`.executeOne({ id: db.pages.id })
        // Retained owner rows with missing/denied source still reach Question unavailable.
        if (!source) return true
        const cards = await trainingFlashcardOptions(ctx, { ...params, flashcardId: candidate.qid }).queryFn()
        if (cards.length === 0) return false
        const pages = await trainingPageOptions(ctx, { ...params, pageId: candidate.pageId }).queryFn()
        return pages.some(page => page.id === candidate.pageId)
      }))
      return queue.filter((_candidate, index) => readable[index])
    },
  }
}

async function findDefaultPage(ctx: FlashcardsFeRouterCtx) {
  const rows = await Promise.all(contentResources(ctx.spaces.snapshot(ctx.getUser().id)).map(resource => resource.queryClient.fetchQuery(spaceTreeOptions(resource))))
  // Any valid page satisfies the default entry (flashcards-editor-home.decisions.md); tree rows are in stable id order.
  return rows.flat()[0]
}

function privateSpace(ctx: FlashcardsFeRouterCtx, workspaceId: string) {
  const resource = ctx.spaces.snapshot(workspaceId).resources.find(value => value.space.kind === 'private' && value.space.ownerUserId === ctx.getUser().id)
  if (!resource) throw new Error('Private space unavailable')
  return resource
}
function workspaceResource(ctx: FlashcardsFeRouterCtx, locator: string) {
  return ctx.spaces.get(locator)
}

async function openRouteWorkspace(ctx: FlashcardsFeRouterCtx, locator: string, allowUnavailablePage = false) {
  if (ctx.spaces.snapshot(locator).resources.length > 0) return
  const directory = await ctx.queryClient.fetchQuery(ctx.rpc.workspace.list.queryOptions())
  if (directory.workspaces.some(workspace => workspace.id === locator)) {
    await ctx.openWorkspace?.(locator)
    return
  }
  if (allowUnavailablePage) return
  throw new Error('Content space unavailable')
}


function readLastPage(storage: Storage, userId: string) {
  const raw = storage.getItem(lastPageKey(userId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as {
      spaceId?: unknown
      id?: unknown
    }
    if (
      typeof parsed.spaceId === 'string' &&
      typeof parsed.id === 'string'
    ) {
      return {
        spaceId: parsed.spaceId,
        id: parsed.id,
      }
    }
  } catch {}
  return null
}

function writeLastPage(
  storage: Storage,
  userId: string,
  page:
    | { spaceId: string; id: string }
    | { spaceId: string; pageId: string },
) {
  if ('pageId' in page) {
    storage.setItem(
      lastPageKey(userId),
      JSON.stringify({ spaceId: page.spaceId, id: page.pageId }),
    )
  } else {
    storage.setItem(lastPageKey(userId), JSON.stringify(page))
  }
}
